import type { SessionTimingConfig } from "./sessionConfig.js";
import { SessionApiException } from "./sessionApi.js";
import type {
  RefreshLease,
  SessionEvents,
  SessionEventType,
} from "./sessionEvents.js";
import type {
  SessionApiError,
  SessionEstablished,
  SessionUser,
} from "./sessionTypes.js";

export type SessionVerifyReason = "boot" | "retry" | "visibility" | "cross-tab";

export type SessionState =
  | { readonly status: "unknown" }
  | {
      readonly status: "probing";
      readonly reason: SessionVerifyReason;
      readonly attempt: number;
    }
  | { readonly status: "refreshing" }
  | {
      readonly status: "authenticated";
      readonly user: SessionUser;
      readonly verifiedAt: number;
    }
  | { readonly status: "anonymous"; readonly reason: string }
  | {
      readonly status: "recoverable";
      readonly error: SessionApiError;
      readonly attempts: number;
    }
  | { readonly status: "logging-out"; readonly attempt: number }
  | {
      readonly status: "logout-pending";
      readonly error: SessionApiError;
      readonly attempts: number;
    }
  | { readonly status: "canceled" };

export interface SessionGate {
  state(): SessionState;
  subscribe(listener: (state: SessionState) => void): () => void;
  verify(reason: SessionVerifyReason): Promise<SessionState>;
  logout(): Promise<SessionState>;
  dispose(): void;
}

export interface SessionApiPort {
  me(signal: AbortSignal): Promise<SessionUser>;
  refresh(signal: AbortSignal): Promise<SessionEstablished>;
  logout(signal: AbortSignal): Promise<void>;
}

export interface ProtectedSurfacePort {
  mount(user: SessionUser): void;
  unmount(): void;
}

export interface VisibilityPort {
  readonly hidden: boolean;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

export type LocalCleanupReason =
  "session-invalid" | "user-logout" | "logout-pending" | "cross-tab-logout";

type TimerHandle = unknown;

export interface SessionGateOptions {
  readonly api: SessionApiPort;
  readonly timings: Readonly<SessionTimingConfig>;
  readonly protectedSurface: ProtectedSurfacePort;
  readonly cleanup: (reason: LocalCleanupReason) => void;
  readonly events?: SessionEvents;
  readonly visibility?: VisibilityPort | null;
  readonly now?: () => number;
  readonly random?: () => number;
  readonly createAbortController?: () => AbortController;
  readonly setTimer?: (callback: () => void, delayMs: number) => TimerHandle;
  readonly clearTimer?: (handle: TimerHandle) => void;
  readonly sleep?: (delayMs: number, signal: AbortSignal) => Promise<void>;
}

function toSessionError(error: unknown): SessionApiError {
  if (error instanceof SessionApiException) return error.info;
  if (error instanceof DOMException && error.name === "TimeoutError") {
    return { kind: "timeout" };
  }
  return { kind: "server" };
}

function retryable(error: SessionApiError): boolean {
  return (
    error.kind === "network" ||
    error.kind === "timeout" ||
    error.kind === "server"
  );
}

function terminalUnauthorized(error: SessionApiError): string | null {
  if (error.kind === "unauthorized" && error.code !== "auth.expired")
    return error.code;
  if (error.kind === "forbidden") return error.code;
  return null;
}

// Native private members let the production minifier shorten internal names.
class SessionGateController implements SessionGate {
  #current: SessionState = { status: "unknown" };
  readonly #listeners = new Set<(state: SessionState) => void>();
  readonly #now: () => number;
  readonly #random: () => number;
  readonly #createAbortController: () => AbortController;
  readonly #setTimer: (
    callback: () => void,
    delayMs: number,
  ) => TimerHandle;
  readonly #clearTimer: (handle: TimerHandle) => void;
  readonly #sleepFn: (
    delayMs: number,
    signal: AbortSignal,
  ) => Promise<void>;
  readonly #visibility: VisibilityPort | null;
  readonly #unsubscribeEvents: (() => void) | null;
  #hiddenAt: number | null = null;
  #generation = 0;
  #activeOperation: AbortController | null = null;
  #activeVerification: AbortController | null = null;
  readonly #backgroundAbort = new DOMException(
    "session verification suspended",
    "AbortError",
  );
  #disposed = false;

  readonly #onVisibilityChange = (): void => {
    if (this.#disposed || !this.#visibility) return;
    if (this.#visibility.hidden) {
      this.#hiddenAt ??= this.#now();
      this.#activeVerification?.abort(this.#backgroundAbort);
      return;
    }

    const hiddenAt = this.#hiddenAt;
    this.#hiddenAt = null;
    if (
      hiddenAt !== null &&
      !this.#activeOperation &&
      this.#current.status === "authenticated" &&
      this.#now() - hiddenAt >= this.#options.timings.visibilityReprobeAfterMs
    ) {
      void this.verify("visibility").catch(() => undefined);
    }
  };

  readonly #options: SessionGateOptions;

  constructor(options: SessionGateOptions) {
    this.#options = options;
    this.#now = options.now ?? Date.now;
    this.#random = options.random ?? Math.random;
    this.#createAbortController =
      options.createAbortController ?? (() => new AbortController());
    this.#setTimer =
      options.setTimer ??
      ((callback, delayMs) => setTimeout(callback, delayMs));
    this.#clearTimer =
      options.clearTimer ?? ((handle) => clearTimeout(handle as number));
    this.#sleepFn =
      options.sleep ??
      ((delayMs, signal) => this.#abortableSleep(delayMs, signal));
    this.#visibility =
      options.visibility === undefined
        ? typeof document === "undefined"
          ? null
          : (document as unknown as VisibilityPort)
        : options.visibility;
    if (this.#visibility) {
      if (this.#visibility.hidden) this.#hiddenAt = this.#now();
      this.#visibility.addEventListener(
        "visibilitychange",
        this.#onVisibilityChange,
      );
    }
    this.#unsubscribeEvents =
      options.events?.subscribe((type) => this.#handleSessionEvent(type)) ??
      null;
  }

  state(): SessionState {
    return this.#current;
  }

  subscribe(listener: (state: SessionState) => void): () => void {
    if (this.#disposed) return () => undefined;
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  async verify(reason: SessionVerifyReason): Promise<SessionState> {
    if (this.#disposed) return this.#current;
    const preserveProtectedSurface =
      reason === "visibility" && this.#current.status === "authenticated";
    const operation = this.#startOperation();
    const operationGeneration = this.#generation;

    try {
      while (this.#isCurrent(operationGeneration)) {
        if (this.#visibility?.hidden) {
          try {
            await this.#waitUntilVisible(operation.signal);
          } catch {
            return this.#current;
          }
        }
        if (!this.#isCurrent(operationGeneration)) return this.#current;
        const verification = this.#createAbortController();
        const onAbort = () => verification.abort(operation.signal.reason);
        operation.signal.addEventListener("abort", onAbort, { once: true });
        this.#activeVerification = verification;
        try {
          return await this.#probe(
            reason,
            verification,
            operationGeneration,
            preserveProtectedSurface,
          );
        } catch (cause) {
          if (verification.signal.reason !== this.#backgroundAbort) throw cause;
        } finally {
          operation.signal.removeEventListener("abort", onAbort);
          if (this.#activeVerification === verification)
            this.#activeVerification = null;
        }
      }
      return this.#current;
    } finally {
      this.#finishOperation(operation);
    }
  }

  async #probe(
    reason: SessionVerifyReason,
    operation: AbortController,
    operationGeneration: number,
    preserveProtectedSurface: boolean,
  ): Promise<SessionState> {
    for (
      let attempt = 1;
      attempt <= this.#options.timings.probeMaxAttempts;
      attempt += 1
    ) {
      if (!this.#isCurrent(operationGeneration)) return this.#current;
      this.#transition(
        { status: "probing", reason, attempt },
        operationGeneration,
        preserveProtectedSurface,
      );

      try {
        const user = await this.#withTimeout(
          operation.signal,
          this.#options.timings.probeTimeoutMs,
          (signal) => this.#options.api.me(signal),
        );
        if (!this.#isCurrent(operationGeneration)) return this.#current;
        this.#transition(
          { status: "authenticated", user, verifiedAt: this.#now() },
          operationGeneration,
        );
        return this.#current;
      } catch (cause) {
        if (!this.#isCurrent(operationGeneration)) return this.#current;
        if (operation.signal.aborted) throw operation.signal.reason;
        const error = toSessionError(cause);
        if (error.kind === "unauthorized" && error.code === "auth.expired") {
          return this.#refreshThenProbe(
            reason,
            operation,
            operationGeneration,
            preserveProtectedSurface,
          );
        }

        const terminalReason = terminalUnauthorized(error);
        if (terminalReason !== null) {
          this.#runCleanup("session-invalid");
          this.#transition(
            { status: "anonymous", reason: terminalReason },
            operationGeneration,
          );
          return this.#current;
        }

        if (
          retryable(error) &&
          attempt < this.#options.timings.probeMaxAttempts
        ) {
          try {
            await this.#sleepFn(this.#retryDelay(attempt), operation.signal);
          } catch {
            if (operation.signal.aborted && this.#isCurrent(operationGeneration))
              throw operation.signal.reason;
            return this.#current;
          }
          if (!this.#isCurrent(operationGeneration)) return this.#current;
          continue;
        }

        this.#transition(
          { status: "recoverable", error, attempts: attempt },
          operationGeneration,
        );
        return this.#current;
      }
    }

    return this.#current;
  }

  async logout(): Promise<SessionState> {
    if (this.#disposed) return this.#current;
    const operation = this.#startOperation();
    const operationGeneration = this.#generation;
    const maxAttempts = this.#options.timings.logoutRetryCount + 1;
    let lastError: SessionApiError = { kind: "server" };

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      this.#transition({ status: "logging-out", attempt }, operationGeneration);
      try {
        await this.#withTimeout(
          operation.signal,
          this.#options.timings.logoutTimeoutMs,
          (signal) => this.#options.api.logout(signal),
        );
        if (!this.#isCurrent(operationGeneration)) return this.#current;
        this.#runCleanup("user-logout");
        this.#transition(
          { status: "anonymous", reason: "user-logout" },
          operationGeneration,
        );
        this.#options.events?.publish("session:logged-out");
        this.#finishOperation(operation);
        return this.#current;
      } catch (cause) {
        if (!this.#isCurrent(operationGeneration)) return this.#current;
        lastError = toSessionError(cause);
        if (retryable(lastError) && attempt < maxAttempts) {
          try {
            await this.#sleepFn(this.#retryDelay(attempt), operation.signal);
          } catch {
            return this.#current;
          }
          if (!this.#isCurrent(operationGeneration)) return this.#current;
          continue;
        }
        break;
      }
    }

    if (this.#isCurrent(operationGeneration)) {
      this.#runCleanup("logout-pending");
      this.#transition(
        { status: "logout-pending", error: lastError, attempts: maxAttempts },
        operationGeneration,
      );
      this.#finishOperation(operation);
    }
    return this.#current;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#generation += 1;
    this.#activeOperation?.abort(
      new DOMException("session gate disposed", "AbortError"),
    );
    this.#activeOperation = null;
    this.#unsubscribeEvents?.();
    this.#visibility?.removeEventListener(
      "visibilitychange",
      this.#onVisibilityChange,
    );
    this.#current = { status: "canceled" };
    this.#safeUnmount();
    this.#notify();
    this.#listeners.clear();
  }

  async #refreshThenProbe(
    reason: SessionVerifyReason,
    operation: AbortController,
    operationGeneration: number,
    preserveProtectedSurface: boolean,
  ): Promise<SessionState> {
    this.#transition(
      { status: "refreshing" },
      operationGeneration,
      preserveProtectedSurface,
    );
    try {
      let refreshLocally = true;
      let refreshLease: RefreshLease | null = null;
      if (this.#options.events) {
        refreshLease = await this.#tryAcquireRefreshLease(operation.signal);
        operation.signal.throwIfAborted();
        if (!this.#isCurrent(operationGeneration)) {
          await refreshLease?.release();
          return this.#current;
        }
        refreshLocally = refreshLease !== null;
        if (!refreshLocally) {
          const outcome = await this.#waitForPeerRefresh(operation.signal);
          if (!this.#isCurrent(operationGeneration)) return this.#current;
          if (outcome === "expired" || outcome === "failed") {
            refreshLease = await this.#tryAcquireRefreshLease(operation.signal);
            operation.signal.throwIfAborted();
            if (!this.#isCurrent(operationGeneration)) {
              await refreshLease?.release();
              return this.#current;
            }
            refreshLocally = refreshLease !== null;
            if (!refreshLocally) {
              const error: SessionApiError = {
                kind: "server",
                code: "auth.refresh_coordination_timeout",
              };
              this.#transition(
                { status: "recoverable", error, attempts: 1 },
                operationGeneration,
              );
              return this.#current;
            }
          }
        }
      }

      if (refreshLocally) {
        try {
          if (refreshLease) {
            const user = await this.#doubleCheckRefreshNeeded(operation.signal);
            if (!this.#isCurrent(operationGeneration)) return this.#current;
            if (user) {
              this.#options.events?.publish("session:refresh-finished");
              this.#transition(
                { status: "authenticated", user, verifiedAt: this.#now() },
                operationGeneration,
              );
              return this.#current;
            }
          }

          this.#options.events?.publish("session:refresh-started");
          await this.#withTimeout(
            operation.signal,
            this.#options.timings.refreshTimeoutMs,
            (signal) => this.#options.api.refresh(signal),
          );
          if (!this.#isCurrent(operationGeneration)) return this.#current;
          this.#options.events?.publish("session:refresh-finished");
        } catch (cause) {
          if (this.#isCurrent(operationGeneration)) {
            this.#options.events?.publish("session:refresh-failed");
          }
          throw cause;
        } finally {
          await refreshLease?.release();
        }
      }
      if (!this.#isCurrent(operationGeneration)) return this.#current;
      this.#transition(
        { status: "probing", reason, attempt: 1 },
        operationGeneration,
        preserveProtectedSurface,
      );
      const user = await this.#withTimeout(
        operation.signal,
        this.#options.timings.probeTimeoutMs,
        (signal) => this.#options.api.me(signal),
      );
      if (!this.#isCurrent(operationGeneration)) return this.#current;
      this.#transition(
        { status: "authenticated", user, verifiedAt: this.#now() },
        operationGeneration,
      );
      return this.#current;
    } catch (cause) {
      if (!this.#isCurrent(operationGeneration)) return this.#current;
      if (operation.signal.aborted) throw operation.signal.reason;
      const error = toSessionError(cause);
      const terminalReason = terminalUnauthorized(error);
      if (terminalReason !== null || error.kind === "unauthorized") {
        const reasonCode =
          terminalReason ??
          (error.kind === "unauthorized" ? error.code : "auth.invalid");
        this.#runCleanup("session-invalid");
        this.#transition(
          { status: "anonymous", reason: reasonCode },
          operationGeneration,
        );
      } else {
        this.#transition(
          { status: "recoverable", error, attempts: 1 },
          operationGeneration,
        );
      }
      return this.#current;
    }
  }

  async #doubleCheckRefreshNeeded(
    signal: AbortSignal,
  ): Promise<SessionUser | null> {
    try {
      return await this.#withTimeout(
        signal,
        this.#options.timings.probeTimeoutMs,
        (probeSignal) => this.#options.api.me(probeSignal),
      );
    } catch (cause) {
      const error = toSessionError(cause);
      if (error.kind === "unauthorized" && error.code === "auth.expired") {
        return null;
      }
      throw cause;
    }
  }

  #handleSessionEvent(type: SessionEventType): void {
    if (this.#disposed) return;
    if (type === "session:logged-out") {
      const operation = this.#startOperation();
      const operationGeneration = this.#generation;
      this.#runCleanup("cross-tab-logout");
      this.#transition(
        { status: "anonymous", reason: "cross-tab-logout" },
        operationGeneration,
      );
      this.#finishOperation(operation);
    } else if (type === "session:authenticated") {
      void this.verify("cross-tab").catch(() => undefined);
    }
  }

  async #tryAcquireRefreshLease(
    signal: AbortSignal,
  ): Promise<RefreshLease | null> {
    if (signal.aborted) return null;
    try {
      return await this.#withTimeout(
        signal,
        this.#options.timings.probeTimeoutMs,
        async (acquisitionSignal) => {
          const lease = await this.#options.events?.tryAcquireRefreshLease();
          if (!lease) return null;
          if (acquisitionSignal.aborted) {
            await lease.release();
            return null;
          }
          let released = false;
          const release = async () => {
            if (released) return;
            released = true;
            signal.removeEventListener("abort", onAbort);
            await lease.release();
          };
          const onAbort = () => {
            void release().catch(() => undefined);
          };
          signal.addEventListener("abort", onAbort, { once: true });
          return { release };
        },
      );
    } catch {
      return null;
    }
  }

  #waitForPeerRefresh(
    signal: AbortSignal,
  ): Promise<"finished" | "failed" | "logged-out" | "expired"> {
    const events = this.#options.events;
    if (!events) return Promise.resolve("expired");

    return new Promise((resolve) => {
      let settled = false;
      let timer: TimerHandle | undefined = undefined;
      let unsubscribe: () => void = () => undefined;
      const onAbort = () => finish("expired");
      const finish = (
        outcome: "finished" | "failed" | "logged-out" | "expired",
      ) => {
        if (settled) return;
        settled = true;
        unsubscribe();
        signal.removeEventListener("abort", onAbort);
        if (timer !== undefined) this.#clearTimer(timer);
        resolve(outcome);
      };
      unsubscribe = events.subscribe((type) => {
        if (type === "session:refresh-finished") finish("finished");
        else if (type === "session:refresh-failed") finish("failed");
        else if (type === "session:logged-out") finish("logged-out");
      });
      const remaining = events.refreshLeaseRemainingMs();
      const waitMs =
        remaining > 0 ? remaining : this.#options.timings.crossTabRefreshLeaseMs;
      timer = this.#setTimer(() => finish("expired"), waitMs);
      if (signal.aborted) onAbort();
      else signal.addEventListener("abort", onAbort, { once: true });
    });
  }

  #startOperation(): AbortController {
    this.#generation += 1;
    this.#activeOperation?.abort(
      new DOMException("session operation superseded", "AbortError"),
    );
    const operation = this.#createAbortController();
    this.#activeOperation = operation;
    return operation;
  }

  #waitUntilVisible(signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        this.#visibility?.removeEventListener("visibilitychange", onVisible);
        signal.removeEventListener("abort", onAbort);
      };
      const onVisible = () => {
        if (this.#visibility?.hidden) return;
        cleanup();
        resolve();
      };
      const onAbort = () => {
        cleanup();
        reject(signal.reason);
      };
      this.#visibility?.addEventListener("visibilitychange", onVisible);
      signal.addEventListener("abort", onAbort, { once: true });
      if (signal.aborted) onAbort();
      else onVisible();
    });
  }

  #finishOperation(operation: AbortController): void {
    if (this.#activeOperation === operation) this.#activeOperation = null;
  }

  #isCurrent(operationGeneration: number): boolean {
    return !this.#disposed && operationGeneration === this.#generation;
  }

  #transition(
    next: SessionState,
    operationGeneration: number,
    preserveProtectedSurface = false,
  ): void {
    if (!this.#isCurrent(operationGeneration)) return;
    this.#current = next;
    if (next.status === "authenticated") {
      try {
        this.#options.protectedSurface.mount(next.user);
      } catch {
        // Surface failures do not change server-verified session truth.
      }
    } else if (!preserveProtectedSurface) {
      this.#safeUnmount();
    }
    this.#notify();
  }

  #notify(): void {
    for (const listener of [...this.#listeners]) {
      try {
        listener(this.#current);
      } catch {
        // One observer cannot prevent other observers or corrupt state.
      }
    }
  }

  #safeUnmount(): void {
    try {
      this.#options.protectedSurface.unmount();
    } catch {
      // Unmount is best-effort; auth state remains fail-closed.
    }
  }

  #runCleanup(reason: LocalCleanupReason): void {
    try {
      this.#options.cleanup(reason);
    } catch {
      // Local cleanup is best-effort and cannot manufacture authentication.
    }
  }

  #retryDelay(failedAttempt: number): number {
    const exponential =
      this.#options.timings.probeRetryBaseMs *
      2 ** Math.max(0, failedAttempt - 1);
    const bounded = Math.min(this.#options.timings.probeRetryMaxMs, exponential);
    const random = Math.min(1, Math.max(0, this.#random()));
    const jitter =
      1 + (random * 2 - 1) * this.#options.timings.probeRetryJitterRatio;
    return Math.round(
      Math.min(this.#options.timings.probeRetryMaxMs, bounded * jitter),
    );
  }

  async #withTimeout<T>(
    operationSignal: AbortSignal,
    timeoutMs: number,
    run: (signal: AbortSignal) => Promise<T>,
  ): Promise<T> {
    const attempt = this.#createAbortController();
    const abortFromOperation = () => attempt.abort(operationSignal.reason);
    if (operationSignal.aborted) abortFromOperation();
    else
      operationSignal.addEventListener("abort", abortFromOperation, {
        once: true,
      });
    const timer = this.#setTimer(
      () =>
        attempt.abort(
          new DOMException("session request timed out", "TimeoutError"),
        ),
      timeoutMs,
    );
    let onAbort: () => void = () => undefined;
    try {
      return await new Promise<T>((resolve, reject) => {
        onAbort = () => reject(attempt.signal.reason);
        attempt.signal.addEventListener("abort", onAbort, { once: true });
        if (attempt.signal.aborted) onAbort();
        else run(attempt.signal).then(resolve, reject);
      });
    } finally {
      attempt.signal.removeEventListener("abort", onAbort);
      this.#clearTimer(timer);
      operationSignal.removeEventListener("abort", abortFromOperation);
    }
  }

  #abortableSleep(delayMs: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(signal.reason);
        return;
      }
      const abort = () => {
        this.#clearTimer(timer);
        reject(signal.reason);
      };
      const timer = this.#setTimer(() => {
        signal.removeEventListener("abort", abort);
        resolve();
      }, delayMs);
      signal.addEventListener("abort", abort, { once: true });
    });
  }
}

export function createSessionGate(options: SessionGateOptions): SessionGate {
  return new SessionGateController(options);
}
